import { useCallback, useSyncExternalStore } from 'react';
import { forget } from './usePoll';

/** One object kept open as a tab of its environment. */
export interface ObjectTab {
    id: string;
    label: string;
}

const KEY = 'v2.objectTabs';
const listeners = new Set<() => void>();
let state: Record<string, ObjectTab[]> = load();

function load(): Record<string, ObjectTab[]> {
    try {
        return JSON.parse(localStorage.getItem(KEY) ?? '{}') ?? {};
    } catch {
        return {};
    }
}

const save = (next: Record<string, ObjectTab[]>) => {
    state = next;
    try {
        localStorage.setItem(KEY, JSON.stringify(next));
    } catch {
        // Not allowed here: the tabs last until the page reloads.
    }
    listeners.forEach((l) => l());
};

const scopeOf = (customer: string, environment: string) => `${customer}/${environment}`.toLowerCase();
const empty: ObjectTab[] = [];

/**
 * The objects open as tabs in one environment: kept in the browser, so they are still there after switching screens,
 * environments or reloading, until closed with their cross.
 */
export const useObjectTabs = (customer: string, environment: string) => {
    const scope = scopeOf(customer, environment);
    const tabs = useSyncExternalStore(
        (l) => {
            listeners.add(l);
            return () => listeners.delete(l);
        },
        () => state[scope] ?? empty
    );

    const open = useCallback(
        (tab: ObjectTab) => {
            const list = state[scope] ?? [];
            const at = list.findIndex((t) => t.id.toLowerCase() === tab.id.toLowerCase());
            if (at >= 0 && list[at].label === tab.label) return;
            save({ ...state, [scope]: at >= 0 ? list.map((t, i) => (i === at ? tab : t)) : [...list, tab] });
        },
        [scope]
    );

    const close = useCallback(
        (id: string) => {
            save({ ...state, [scope]: (state[scope] ?? []).filter((t) => t.id.toLowerCase() !== id.toLowerCase()) });
            const tail = `/${scope}/${id}`.toLowerCase();
            forget((key) => key.toLowerCase().endsWith(tail));
        },
        [scope]
    );

    return { tabs, open, close };
};
